<template>
    <div class="tw:grid tw:grid-cols-1 tw:gap-4 tw:sm:grid-cols-2 tw:xl:grid-cols-4" data-cy="resumen-ventas-kpis">
        <KpiVentaCard
            label="Recaudación total"
            :tooltip="tooltips.recaudacion"
            icon="fas fa-money-bill-wave"
            icon-wrapper-class="tw:bg-blue-50 tw:text-blue-600"
            :value="importe(totales.recaudacion)"
            footnote="Entradas emitidas"
            :badge="`${totales.totalEntradas} tickets`"
            badge-class="tw:bg-slate-100 tw:text-slate-600"
            :loading="props.loading"
            data-cy="kpi-recaudacion"
        />

        <KpiVentaCard
            label="Ventas online web"
            :tooltip="tooltips.online"
            icon="fas fa-globe"
            icon-wrapper-class="tw:bg-blue-50 tw:text-blue-600"
            :value="importe(totales.online.importe)"
            footnote="Canal online"
            :badge="badgeCanal('online')"
            badge-class="tw:bg-blue-50 tw:text-blue-700"
            :loading="props.loading"
            data-cy="kpi-online"
        />

        <KpiVentaCard
            label="Taquilla física"
            :tooltip="tooltips.taquilla"
            icon="fas fa-store"
            icon-wrapper-class="tw:bg-emerald-50 tw:text-emerald-600"
            :value="importe(totales.taquilla.importe)"
            footnote="Venta presencial"
            :badge="badgeCanal('taquilla')"
            badge-class="tw:bg-emerald-50 tw:text-emerald-700"
            :loading="props.loading"
            data-cy="kpi-taquilla"
        />

        <KpiVentaCard
            label="Invitaciones"
            :tooltip="tooltips.invitaciones"
            icon="fas fa-ticket-alt"
            icon-wrapper-class="tw:bg-amber-50 tw:text-amber-600"
            :value="`${totales.invitaciones.entradas} emitidas`"
            footnote="Importe"
            :badge="badgeInvitaciones"
            badge-class="tw:bg-amber-50 tw:text-amber-700"
            :loading="props.loading"
            data-cy="kpi-invitaciones"
        />
    </div>
</template>

<script setup>
    import { computed } from 'vue';
    import appFilters from '@/filters';

    import KpiVentaCard from '@/modules/admin/eventos/resumen-ventas/components/KpiVentaCard.vue';
    import { porcentajesSobreTotal } from '@/composables/admin/eventos/resumenVentasAgregados';

    const props = defineProps({
        /**
         * Totales de la PÁGINA cargada (ver `totalesPagina`). No hay agregado
         * global en el backend, y la fila de totales de la tabla lo declara
         * explícitamente ("en pantalla") para no dar a entender otra cosa.
         */
        totales: {
            type: Object,
            required: true
        },
        loading: {
            type: Boolean,
            default: false
        },
        /**
         * `true` cuando `totales` es el total del RANGO filtrado (lo entrega el
         * backend). Mientras sea `false` las tarjetas muestran ceros y el
         * tooltip lo avisa: no se puede decir "de todo el rango" mostrando 0.
         */
        globales: {
            type: Boolean,
            default: false
        }
    });

    const importe = appFilters.formatear_precio;

    /** Alcance de cada dato, para que el tooltip no mienta. */
    /**
     * Texto de ayuda de cada tarjeta: qué dato muestra, en una línea y sin
     * jerga técnica. El alcance sólo se nombra cuando el número es el del rango
     * filtrado (mientras el backend no lo entregue, explicar un alcance sería
     * describir algo que la tarjeta todavía no muestra).
     */
    const alcance = computed(() => (props.globales ? ' Incluye todos los eventos del rango filtrado.' : ''));

    const tooltips = computed(() => ({
        recaudacion: `Suma de las ventas de taquilla y online. Las invitaciones no suman importe.${alcance.value}`,
        online: `Entradas vendidas por internet (web) y su importe.${alcance.value}`,
        taquilla: `Entradas vendidas en taquilla y su importe.${alcance.value}`,
        invitaciones: `Entradas de invitación emitidas. No llevan importe.${alcance.value}`
    }));

    /**
     * Porcentaje de cada canal sobre el TOTAL DE ENTRADAS EMITIDAS (los tres
     * canales). Se calculan los tres a la vez para que sumen exactamente 100:
     * antes sólo se pintaban taquilla y online y el peso de las invitaciones
     * desaparecía, así que la suma quedaba por debajo de 100.
     */
    const porcentajes = computed(() => porcentajesSobreTotal([
        props.totales.taquilla.entradas,
        props.totales.online.entradas,
        props.totales.invitaciones.entradas
    ]));

    /**
     * Badge de un canal de venta: "66 tickets (53,7%)". Sin entradas no hay
     * porcentaje que enseñar, así que se muestra "—" en vez de un 0% falso.
     *
     * @param {string} canal 'taquilla' | 'online'
     * @returns {string}
     */
    function badgeCanal(canal) {
        const entradas = props.totales[canal].entradas;
        const posicion = canal === 'taquilla' ? 0 : 1;
        const reparto = porcentajes.value[posicion];

        return reparto === null
            ? `${entradas} tickets`
            : `${entradas} tickets (${formatearPorcentaje(reparto)})`;
    }

    /** El recuento de invitaciones va en grande: su importe real es siempre 0. */
    const badgeInvitaciones = computed(() => {
        const reparto = porcentajes.value[2];

        return reparto === null
            ? importe(props.totales.invitaciones.importe)
            : `${importe(props.totales.invitaciones.importe)} (${formatearPorcentaje(reparto)})`;
    });

    /**
     * Los decimales se muestran con coma (es-ES), igual que el resto de la UI.
     *
     * @param {number} valor
     * @returns {string}
     */
    function formatearPorcentaje(valor) {
        return `${String(valor).replace('.', ',')}%`;
    }
</script>
